// ObservabilityView — Phase 7c admin dashboard for the autonomous
// stack. Polls /api/admin/observability and surfaces:
//
//   · ASCEND runs by state (counts + needs_human list + stuck queue)
//   · Per-bucket rate-limit usage + currently-exhausted tenant count
//   · Top rejected tenants in the last 24h
//   · Rejection rate in the last hour
//
// Visible only to admins (the endpoint gates; this component also
// renders an "Admins only" empty state for non-admin users).

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import {
  Loader2, RefreshCcw, AlertTriangle, CheckCircle2, Clock, Activity, Shield, RotateCcw, XCircle, DollarSign,
} from 'lucide-react';

function fmtCents(c) {
  const dollars = (c || 0) / 100;
  if (dollars >= 1000) return `$${(dollars / 1000).toFixed(1)}k`;
  if (dollars >= 100)  return `$${dollars.toFixed(0)}`;
  return `$${dollars.toFixed(2)}`;
}
import { supabase } from '../lib/supabase';
import SentryFindingsPanel from '../audit/SentryFindingsPanel';

async function fetchObservability() {
  const jwt = readPersistedJwtDirect();
  const res = await fetch('/api/admin/observability', {
    headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
  });
  const body = await res.json().catch(() => ({}));
  if (res.status === 403) return { forbidden: true };
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

async function ascendAction(runId, action, reason) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch('/api/admin/ascend-action', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
    body: JSON.stringify({ run_id: runId, action, reason }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

function Kpi({ label, value, sub, tone = 'slate', icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 flex items-center gap-1.5">
        {icon}{label}
      </div>
      <div className={`text-2xl font-semibold tabular-nums mt-1 text-${tone}-700`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function StateBadge({ state }) {
  const tones = {
    queued:        'bg-slate-100 text-slate-700',
    in_progress:   'bg-amber-100 text-amber-800',
    ready:         'bg-emerald-100 text-emerald-800',
    needs_human:   'bg-rose-100 text-rose-800',
    cancelled:     'bg-slate-100 text-slate-500',
  };
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${tones[state] || 'bg-slate-100 text-slate-700'}`}>
      {state}
    </span>
  );
}

export default function ObservabilityView() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await fetchObservability();
      setData(r);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [load]);

  if (loading && !data) {
    return (
      <div className="max-w-none mx-auto px-4 py-8 text-center text-slate-500">
        <Loader2 size={20} className="animate-spin inline mr-2"/>Loading observability…
      </div>
    );
  }
  if (data?.forbidden) {
    return (
      <div className="max-w-none mx-auto px-4 py-8">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-sm text-amber-900">
          <div className="font-semibold mb-1">Admins only</div>
          <div>This dashboard is gated to admin role accounts.</div>
        </div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="max-w-none mx-auto px-4 py-8">
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-sm text-rose-900">
          <div className="font-semibold mb-1">Failed to load observability</div>
          <div className="font-mono text-xs mt-1">{err}</div>
          <button onClick={load} className="mt-3 bg-rose-700 hover:bg-rose-800 text-white text-xs font-semibold px-3 py-1.5 rounded">Retry</button>
        </div>
      </div>
    );
  }

  const ascend = data?.ascend || {};
  const rl = data?.rate_limits || {};
  const cost = data?.cost || {};
  const byState = ascend.runs_by_state || {};

  return (
    <div className="max-w-none mx-auto px-4 py-6 space-y-6">
      {/* SENTRY continuous audit — surfaced at the top so it's the
          first thing an admin sees when opening Observability. */}
      <SentryFindingsPanel/>

      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500">Phase 7c · ops dashboard</div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight mt-1">Autonomous-stack observability</h1>
          <div className="text-sm text-slate-500 mt-1">
            Per-tenant rate-limit buckets + ASCEND state machine health.
            {data?.fetched_at && <span> · Last fetched {new Date(data.fetched_at).toLocaleTimeString()}</span>}
          </div>
        </div>
        <button onClick={load} disabled={loading}
          className="text-sm font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5 disabled:opacity-30">
          <RefreshCcw size={14}/> Refresh
        </button>
      </div>

      {/* ASCEND KPIs */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">ASCEND runs</div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Kpi label="Total" value={ascend.total_runs || 0} tone="slate" icon={<Activity size={11}/>}/>
          <Kpi label="Queued" value={byState.queued || 0} tone="slate"/>
          <Kpi label="In progress" value={byState.in_progress || 0} tone="amber"/>
          <Kpi label="Ready" value={byState.ready || 0} tone="emerald" icon={<CheckCircle2 size={11}/>}/>
          <Kpi label="Needs human" value={byState.needs_human || 0}
               tone={(byState.needs_human || 0) > 0 ? 'rose' : 'slate'}
               icon={<AlertTriangle size={11}/>}/>
        </div>
      </div>

      {/* Rate-limit KPIs */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
          <Shield size={12}/> Rate limits
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Kpi label="Rejections (1h)" value={rl.rejected_in_last_hour || 0}
               tone={(rl.rejected_in_last_hour || 0) > 0 ? 'rose' : 'emerald'}/>
          <Kpi label="Tenants over any bucket" value={(rl.top_rejected_24h || []).length} tone="amber" sub="last 24h"/>
          <Kpi label="Buckets in use" value={(rl.bucket_summary || []).length} tone="slate"/>
        </div>
      </div>

      {/* Per-tenant $-spend (MTD) */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
          <DollarSign size={12}/> Platform spend (month-to-date)
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Kpi label="Total platform spend" value={fmtCents(cost.platform_total_cents)} tone="slate"
               sub={cost.month_start ? `Since ${new Date(cost.month_start).toLocaleDateString()}` : null}/>
          <Kpi label="Tenants with spend" value={cost.tenant_count_with_spend || 0} tone="slate"/>
          <Kpi label="Top spender" value={
            cost.top_spenders_mtd?.[0]
              ? fmtCents(cost.top_spenders_mtd[0].total_cents)
              : '$0'
          } tone={
            (cost.top_spenders_mtd?.[0]?.total_cents || 0) > 10000 ? 'rose'   // >$100/mo
              : (cost.top_spenders_mtd?.[0]?.total_cents || 0) > 5000 ? 'amber' // >$50/mo
              : 'slate'
          } sub={cost.top_spenders_mtd?.[0]?.owner_id ? `${cost.top_spenders_mtd[0].owner_id.slice(0, 12)}…` : null}/>
        </div>
      </div>

      {(cost.top_spenders_mtd || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Top 25 spenders this month</div>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500">
              <tr className="border-b border-slate-100">
                <th className="text-left py-2 px-2">Owner</th>
                <th className="text-right py-2 px-2">Total MTD</th>
                <th className="text-left py-2 px-2">By reason</th>
              </tr>
            </thead>
            <tbody>
              {cost.top_spenders_mtd.map((s, i) => {
                const tone = s.total_cents > 10000 ? 'text-rose-700 font-semibold'
                          : s.total_cents > 5000  ? 'text-amber-700 font-semibold'
                          :                          'text-slate-800';
                const reasons = Object.entries(s.by_reason || {})
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, v]) => `${k}: ${fmtCents(v)}`)
                  .join(' · ');
                return (
                  <tr key={`${s.owner_id}_${i}`} className="border-b border-slate-50">
                    <td className="py-2 px-2 font-mono text-xs text-slate-700">{s.owner_id?.slice(0, 12)}…</td>
                    <td className={`py-2 px-2 text-right tabular-nums ${tone}`}>{fmtCents(s.total_cents)}</td>
                    <td className="py-2 px-2 text-xs text-slate-500">{reasons || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Bucket summary table */}
      {(rl.bucket_summary || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Per-bucket totals (lifetime)</div>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500">
              <tr className="border-b border-slate-100">
                <th className="text-left py-2 px-2">Bucket</th>
                <th className="text-right py-2 px-2">Tenants</th>
                <th className="text-right py-2 px-2">Consumed</th>
                <th className="text-right py-2 px-2">Rejected</th>
                <th className="text-right py-2 px-2">Currently empty</th>
              </tr>
            </thead>
            <tbody>
              {rl.bucket_summary.map(b => (
                <tr key={b.bucket} className="border-b border-slate-50">
                  <td className="py-2 px-2 font-mono text-slate-800">{b.bucket}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{b.tenants}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{Math.round(b.total_consumed).toLocaleString()}</td>
                  <td className={`py-2 px-2 text-right tabular-nums ${b.total_rejected > 0 ? 'text-rose-700 font-semibold' : ''}`}>
                    {b.total_rejected.toLocaleString()}
                  </td>
                  <td className={`py-2 px-2 text-right tabular-nums ${b.currently_exhausted > 0 ? 'text-amber-700 font-semibold' : ''}`}>
                    {b.currently_exhausted}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Top rejected tenants (last 24h) */}
      {(rl.top_rejected_24h || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Top rejected tenants (last 24h)</div>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500">
              <tr className="border-b border-slate-100">
                <th className="text-left py-2 px-2">Owner</th>
                <th className="text-left py-2 px-2">Bucket</th>
                <th className="text-right py-2 px-2">Total rejected</th>
                <th className="text-right py-2 px-2">Last rejected</th>
                <th className="text-right py-2 px-2">Empty?</th>
              </tr>
            </thead>
            <tbody>
              {rl.top_rejected_24h.map((r, i) => (
                <tr key={`${r.owner_id}_${r.bucket}_${i}`} className="border-b border-slate-50">
                  <td className="py-2 px-2 font-mono text-xs text-slate-700">{r.owner_id?.slice(0, 12)}…</td>
                  <td className="py-2 px-2 font-mono text-slate-800">{r.bucket}</td>
                  <td className="py-2 px-2 text-right tabular-nums text-rose-700 font-semibold">{r.total_rejected.toLocaleString()}</td>
                  <td className="py-2 px-2 text-right text-xs text-slate-500">{r.last_rejected_at ? new Date(r.last_rejected_at).toLocaleString() : '—'}</td>
                  <td className="py-2 px-2 text-right text-xs">{r.currently_exhausted ? <span className="text-rose-700 font-semibold">YES</span> : <span className="text-slate-400">no</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Stuck queue */}
      {(ascend.stuck_in_queue || []).length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-amber-900 mb-3 flex items-center gap-1.5">
            <Clock size={12}/> Stuck in queue (>30 min, cron not advancing)
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs text-amber-800">
              <tr className="border-b border-amber-200">
                <th className="text-left py-2 px-2">Owner</th>
                <th className="text-left py-2 px-2">State</th>
                <th className="text-right py-2 px-2">Attempts</th>
                <th className="text-right py-2 px-2">Age (min)</th>
                <th className="text-right py-2 px-2">Last advance</th>
              </tr>
            </thead>
            <tbody>
              {ascend.stuck_in_queue.map(r => (
                <tr key={r.id} className="border-b border-amber-100">
                  <td className="py-2 px-2 font-mono text-xs text-slate-700">{r.owner_id?.slice(0, 12)}…</td>
                  <td className="py-2 px-2"><StateBadge state={r.state}/></td>
                  <td className="py-2 px-2 text-right tabular-nums">{r.total_attempts}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{r.age_minutes}</td>
                  <td className="py-2 px-2 text-right text-xs text-slate-500">{r.last_advanced_at ? new Date(r.last_advanced_at).toLocaleString() : 'never'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Needs human */}
      {(ascend.needs_human || []).length > 0 && (
        <NeedsHumanTable rows={ascend.needs_human} onAction={load}/>
      )}

      {/* Empty state when everything is clean — see also NeedsHumanTable below */}
      {(byState.needs_human || 0) === 0 &&
       (ascend.stuck_in_queue || []).length === 0 &&
       (rl.rejected_in_last_hour || 0) === 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center text-sm text-emerald-900">
          <CheckCircle2 size={20} className="inline mr-2"/>
          The autonomous stack is healthy — no stuck runs, no rejections in the last hour.
        </div>
      )}
    </div>
  );
}

// ── Needs-human table with self-service action buttons (Phase 9) ──
function NeedsHumanTable({ rows, onAction }) {
  const [busyId, setBusyId] = React.useState(null);
  const [feedback, setFeedback] = React.useState(null);  // { id, message, tone }

  async function fire(runId, action, label) {
    const reason = window.prompt(`Reason for "${label}" (optional, will be logged):`, '');
    if (reason === null) return; // operator hit cancel on the prompt
    setBusyId(runId);
    setFeedback(null);
    try {
      const r = await ascendAction(runId, action, reason || null);
      setFeedback({ id: runId, tone: 'ok', message: `${label} → ${r.state_after}` });
      // Refresh the outer dashboard so the row disappears (or moves
      // out of needs_human) without a manual click.
      if (onAction) await onAction();
    } catch (e) {
      setFeedback({ id: runId, tone: 'err', message: `Failed: ${e.message}` });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-rose-900 mb-3 flex items-center gap-1.5">
        <AlertTriangle size={12}/> Needs human intervention · {rows.length}
      </div>
      <table className="w-full text-sm">
        <thead className="text-xs text-rose-800">
          <tr className="border-b border-rose-200">
            <th className="text-left py-2 px-2">Owner</th>
            <th className="text-left py-2 px-2">Failed step</th>
            <th className="text-left py-2 px-2">Last error</th>
            <th className="text-right py-2 px-2">Attempts</th>
            <th className="text-right py-2 px-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <React.Fragment key={r.id}>
              <tr className="border-b border-rose-100">
                <td className="py-2 px-2 font-mono text-xs text-slate-700">{r.owner_id?.slice(0, 12)}…</td>
                <td className="py-2 px-2 font-mono text-slate-800">{r.failed_step || '—'}</td>
                <td className="py-2 px-2 font-mono text-xs text-rose-700 max-w-md truncate" title={r.last_error || ''}>{r.last_error || '—'}</td>
                <td className="py-2 px-2 text-right tabular-nums">{r.total_attempts}</td>
                <td className="py-2 px-2 text-right">
                  <div className="inline-flex gap-1.5">
                    <button
                      onClick={() => fire(r.id, 'retry', 'Retry failed step')}
                      disabled={busyId === r.id}
                      title="Reset the failed step to pending so the cron re-runs it. Use after you've fixed the underlying cause (e.g., filled in business info, set an env var)."
                      className="text-xs font-medium bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 px-2 py-1 rounded inline-flex items-center gap-1 disabled:opacity-40"
                    >
                      {busyId === r.id ? <Loader2 size={11} className="animate-spin"/> : <RotateCcw size={11}/>}
                      Retry
                    </button>
                    <button
                      onClick={() => fire(r.id, 'retry_all', 'Reset all failed steps')}
                      disabled={busyId === r.id}
                      title="Reset every failed step to pending and re-queue the run. Use when multiple steps have stalled."
                      className="text-xs font-medium bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 px-2 py-1 rounded inline-flex items-center gap-1 disabled:opacity-40"
                    >
                      Retry all
                    </button>
                    <button
                      onClick={() => fire(r.id, 'cancel', 'Cancel run')}
                      disabled={busyId === r.id}
                      title="Mark this run cancelled. The record stays for audit; ASCEND won't touch it again."
                      className="text-xs font-medium bg-white border border-rose-200 hover:bg-rose-100 text-rose-700 px-2 py-1 rounded inline-flex items-center gap-1 disabled:opacity-40"
                    >
                      <XCircle size={11}/>
                      Cancel
                    </button>
                  </div>
                </td>
              </tr>
              {feedback?.id === r.id && (
                <tr>
                  <td colSpan={5} className={`px-2 py-1 text-xs ${feedback.tone === 'ok' ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {feedback.message}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
